import React from 'react';
import { Chip } from '@jotpsych/ui';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';

import { useEventTracking } from '../../../services/statsig';
import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';

interface TierCardProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  isSelected: boolean;
  isRecommended?: boolean;
  /**
   * Marks this tier as the user's (or member's) currently-paid tier. Stays anchored to the
   * original tier even when a different card is selected — visual anchor for comparison.
   * Independent of `isSelected`.
   */
  isCurrent?: boolean;
  onSelect?: (bundleId: string) => void;
  hidePrice?: boolean;
  hideButton?: boolean;
}

export const TierCard: React.FC<TierCardProps> = ({
  bundle: rawBundle,
  billingInterval,
  isSelected,
  isRecommended = false,
  isCurrent = false,
  onSelect,
  hidePrice = false,
  hideButton = false,
}) => {
  const { subscription } = useEventTracking();
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;

  // Calculate monthly equivalent for annual billing
  const price =
    billingInterval === 'month'
      ? bundle.monthly_unit_amount
      : Math.round(bundle.yearly_unit_amount / 12);
  const formattedPrice = (price / 100).toFixed(0);

  const features = bundle.marketing_features || [];

  const handleClick = () => {
    if (!onSelect) return;

    // Track tier card click (interaction)
    subscription.tierCardClicked({
      tier_id: bundle.bundle_id,
      tier_name: bundle.name,
      monthly_price: bundle.monthly_unit_amount,
      yearly_price: bundle.yearly_unit_amount,
    });

    onSelect(bundle.bundle_id);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: '12px',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        '&:hover': {
          borderColor: 'primary.light',
          transform: 'translateY(-4px)',
          boxShadow: 3,
        },
      }}
    >
      {isCurrent && (
        <Chip
          label="Current"
          color="primary"
          variant="outlined"
          size="xSmall"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1,
          }}
        />
      )}

      {/* {isRecommended && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            px: 2,
            py: 0.5,
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          RECOMMENDED
        </Box>
      )} */}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
        }}
      >
        {/* Tier Name */}
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: 2,
            textAlign: 'center',
          }}
        >
          {bundle.name}
        </Typography>

        {/* Price */}
        {!hidePrice && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h3" component="div" sx={{ fontWeight: 700 }}>
              ${formattedPrice}
              <Typography component="span" variant="h6" sx={{ color: 'text.secondary' }}>
                /mo
              </Typography>
            </Typography>
            {billingInterval === 'year' && (
              <Typography variant="caption" color="text.secondary">
                Billed annually
              </Typography>
            )}
          </Box>
        )}

        {/* Features List */}
        <Box sx={{ flex: 1, mb: 3 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1.5,
              }}
            >
              {feature.included ? (
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.25rem', mt: 0.25 }} />
              ) : (
                <CancelIcon sx={{ color: 'text.disabled', fontSize: '1.25rem', mt: 0.25 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  color: feature.included ? 'text.primary' : 'text.secondary',
                }}
              >
                {feature.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Subscribe Button */}
        {!hideButton && onSelect && (
          <Button
            variant={isRecommended ? 'contained' : 'outlined'}
            color="primary"
            size="large"
            fullWidth
            onClick={handleClick}
            sx={{
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              ...(isRecommended && {
                backgroundColor: 'primary.dark',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              }),
            }}
          >
            {isSelected ? 'Selected' : 'Subscribe'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
